import React, {useState, useEffect} from 'react';
import {Box, Stack, Collapse} from '@mui/material';
import {
  Input,
  Button,
  Headline,
  Body,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';
import {JsonApiError} from '@/utils/jsonapiClient';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {type BrandInstagramBusinessAccount} from '@/types/brandedContentAdsPartnership';
import Link from '@/components/link';
import useUpdateBrandInstagramBusinessAccount from '@/hooks/mutations/use-update-brand-instagram-business-account';
import useBrandId from '@/hooks/use-brand-id';
import styles from './InstagramAccountConfigCard.module.scss';

interface InstagramAccountConfigCardProps {
  testID?: string;
  brandInstagramBusinessAccount?: BrandInstagramBusinessAccount | null;
}

function InstagramAccountConfigCard({
  testID = 'instagram-account-config-card',
  brandInstagramBusinessAccount,
}: InstagramAccountConfigCardProps) {
  const brandId = useBrandId();
  const {mutateAsync: updateAdsAccountId, isLoading: isSaving} =
    useUpdateBrandInstagramBusinessAccount();
  const [adsManagerAccountId, setAdsManagerAccountId] = useState(
    brandInstagramBusinessAccount?.adsAccountId || ''
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Sync local state when prop changes
  useEffect(() => {
    if (!isEditing) {
      setAdsManagerAccountId(brandInstagramBusinessAccount?.adsAccountId || '');
    }
    // We intentionally avoid rerunning when toggling edit mode so we don't overwrite unsaved changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandInstagramBusinessAccount?.adsAccountId]);

  const handleSave = async () => {
    if (!brandId || !brandInstagramBusinessAccount?.id) {
      return;
    }

    try {
      const sanitizedAdsAccountId = adsManagerAccountId.trim();
      await updateAdsAccountId({
        brandId,
        brandInstagramBusinessAccountId: brandInstagramBusinessAccount.id,
        adsAccountId: sanitizedAdsAccountId,
      });
      setAdsManagerAccountId(sanitizedAdsAccountId);
      setIsEditing(false);
    } catch (error) {
      const rawStatus =
        error instanceof JsonApiError
          ? error.status
          : ((error as {status?: number | string | null})?.status ??
            (error as {response?: {status?: number | string}})?.response
              ?.status);
      const statusNumber =
        typeof rawStatus === 'string' ? Number(rawStatus) : rawStatus;

      if (statusNumber === 422) {
        raiseFlashMessageError({
          title: translate('Use a valid Facebook Ads Account ID'),
          subtitle: translate('Please try again or come back later'),
          icon: (
            <Icon
              size="large"
              appearance="danger"
              name="Navigation-Attention"
            />
          ),
        });
        return;
      }
      raiseFlashMessageError();
    }
  };

  const handleCancel = () => {
    setAdsManagerAccountId(brandInstagramBusinessAccount?.adsAccountId || '');
    setIsEditing(false);
  };

  const handleEdit = () => {
    setAdsManagerAccountId(brandInstagramBusinessAccount?.adsAccountId || '');
    setIsEditing(true);
  };

  return (
    <Box
      className={styles.cardContainer}
      data-testid={testID}
      sx={{
        backgroundColor: designSystemToken('semantic.bg.secondary'),
        border: `1px solid ${designSystemToken('semantic.bg.tertiary')}`,
      }}
    >
      {/* Collapsible Header */}
      <Box
        className={styles.collapsibleHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: isExpanded ? '16px' : '0',
          transition: 'padding-bottom 0.2s ease',
        }}
        data-testid={`${testID}--header`}
      >
        <Stack gap="4px">
          <Headline size="lg" color={designSystemToken('semantic.fg.primary')}>
            {translate('Account Configuration')}
          </Headline>
          {!isExpanded && (
            <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
              {translate(
                'Manage your Instagram Business Account settings to enable Partnership Ads.'
              )}
            </Body>
          )}
        </Stack>
        <Box
          sx={{
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icon
            size="medium"
            appearance="neutral"
            name="Actions-Arrow-Down-Large"
          />
        </Box>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={isExpanded}>
        <Stack gap="24px">
          <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
            {translate(
              'Manage your Instagram Business Account settings to enable Partnership Ads. Connect your Ads Manager account and verify your Facebook Page connection.'
            )}
          </Body>

          {/* Configuration Fields */}
          <Stack gap="20px" className={styles.fieldsContainer}>
            {/* Instagram Page - Read Only */}
            <div className={styles.fieldItem}>
              <Box
                component="label"
                className={styles.label}
                sx={{color: designSystemToken('semantic.fg.primary')}}
              >
                {translate('Instagram Page')}
              </Box>
              {brandInstagramBusinessAccount?.cachedHandle ? (
                <Box
                  className={styles.valueDisplay}
                  sx={{
                    backgroundColor: designSystemToken('semantic.bg.primary'),
                    border: `1px solid ${designSystemToken('semantic.bg.tertiary')}`,
                  }}
                >
                  <Body
                    size="md"
                    color={designSystemToken('semantic.fg.primary')}
                  >
                    {brandInstagramBusinessAccount.cachedHandle}
                  </Body>
                </Box>
              ) : (
                <Box>
                  <Body
                    size="md"
                    color={designSystemToken('semantic.fg.secondary')}
                  >
                    {translate('No Instagram Business Account configured.')}{' '}
                    <Link
                      to="/organization-settings/brands"
                      style={{color: designSystemToken('semantic.fg.link')}}
                    >
                      {translate('Go to organization settings')}
                    </Link>
                  </Body>
                </Box>
              )}
              <Box
                component="p"
                className={styles.helperText}
                sx={{color: designSystemToken('semantic.fg.tertiary')}}
              >
                {translate(
                  'This is automatically linked to your Instagram Business Account and cannot be changed here.'
                )}
              </Box>
            </div>

            {/* Ads Manager Account ID - Editable */}
            <div className={styles.fieldItem}>
              <Box
                component="label"
                className={styles.label}
                sx={{color: designSystemToken('semantic.fg.primary')}}
              >
                {translate('Ads Manager Account ID')}
              </Box>
              {isEditing ? (
                <Stack gap="12px">
                  <Input
                    type="text"
                    value={adsManagerAccountId}
                    onInputChange={setAdsManagerAccountId}
                    placeholder={translate('Enter your Ads Manager Account ID')}
                    testID={`${testID}--ads-manager-input`}
                    className={styles.input}
                  />
                  <Box sx={{display: 'flex', gap: '8px', flexDirection: 'row'}}>
                    <Button
                      appearance="neutral"
                      mode="filled"
                      size="medium"
                      onClick={handleSave}
                      disabled={
                        isSaving ||
                        !brandInstagramBusinessAccount?.id ||
                        !brandId
                      }
                      testID={`${testID}--save-button`}
                    >
                      {isSaving
                        ? translate('Saving...')
                        : translate('Save Changes')}
                    </Button>
                    <Button
                      appearance="neutral"
                      mode="filled"
                      size="medium"
                      onClick={handleCancel}
                      disabled={isSaving}
                      testID={`${testID}--cancel-button`}
                    >
                      {translate('Cancel')}
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    flexDirection: 'row',
                  }}
                >
                  <Box
                    className={styles.valueDisplay}
                    sx={{
                      backgroundColor: designSystemToken('semantic.bg.primary'),
                      border: `1px solid ${designSystemToken('semantic.bg.tertiary')}`,
                    }}
                  >
                    <Body
                      size="md"
                      color={
                        adsManagerAccountId
                          ? designSystemToken('semantic.fg.primary')
                          : designSystemToken('semantic.fg.tertiary')
                      }
                    >
                      {adsManagerAccountId || translate('Not configured')}
                    </Body>
                  </Box>
                  <Button
                    appearance="neutral"
                    mode="filled"
                    size="small"
                    onClick={handleEdit}
                    testID={`${testID}--edit-button`}
                  >
                    {translate('Edit')}
                  </Button>
                </Box>
              )}
              <Box
                component="p"
                className={styles.helperText}
                sx={{color: designSystemToken('semantic.fg.tertiary')}}
              >
                {translate(
                  'The Ads Manager Account ID is required to create and manage Partnership Ads.'
                )}
              </Box>
            </div>
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
}

export default InstagramAccountConfigCard;
